import React, { type HTMLAttributes, type ReactNode } from "react"

import { Loading } from "~/components/reusable/Loading"
import { cn } from "~/utils/helpers"

interface Props extends HTMLAttributes<HTMLDivElement> {
  className?: string
  children?: ReactNode
}
/**
 * `Skeleton` provides a placeholder representation during content loading.
 * It displays an animated skeleton view with a loading 'blip' by default. If children are provided, it renders the children instead,
 * making it versatile for various loading states or as a simple container.
 *
 * @param {Props} props The props for the Skeleton component.
 * @param {string} [props.className] Additional CSS classes for custom styling.
 * @param {ReactNode} [props.children] The content to be rendered inside the Skeleton.
 * @returns {React.ReactElement} A React element representing the skeleton or the provided children.
 */
const Skeleton: React.FC<Props> = ({ className, children, ...props }) => {
  return (
    <div
      className={cn(
        "relative animate-pulse rounded-lg shadow-[0_2px_10px_0_rgba(0,0,0,0.05)] bg-component-hover",
        className,
      )}
      {...props}
    >
      {!children && (
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <Loading size="sm" className="text-fg-muted" />
        </div>
      )}

      {children}
    </div>
  )
}

export { Skeleton }
